import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuthContext } from '../../../hooks/useAuthContext';
import { getReplies } from '../../../services/firebase/forum';
import { formatDistanceToNow } from 'date-fns';
import { 
  collection, 
  query, 
  where, 
  orderBy, 
  onSnapshot 
} from 'firebase/firestore';
import { db } from '../../../services/firebase/firebase-config';
import { 
  UserIcon,
  ChatBubbleLeftRightIcon,
  PencilIcon
} from '@heroicons/react/24/outline';
import LoadingSkeleton from '../../common/LoadingSkeleton';
import LikeButton from './LikeButton';
import AuthorBadge from './AuthorBadge';
import ModerationControls from './ModerationControls';
import DeletionControls from './DeletionControls';
import { deleteReply, updateReply } from '../../../services/firebase/forum';
import ReplyModal from './ReplyModal';
import OriginalPostPreview from './OriginalPostPreview';
import ReplyComposer from './ReplyComposer';
import useReplyModal from '../../../hooks/useReplyModal';
import Button from '../../common/Button';
import MobileReplyActions from './MobileReplyActions';

import UserMentionInput from '../../common/UserMentionInput';
import ContentWithMentions from '../../common/ContentWithMentions';
import AttachmentDisplay from './AttachmentDisplay';
import useCurrentProfilePicture from '../../../hooks/useCurrentProfilePicture';


const ReplyCard = React.memo(({ reply, onLikeUpdate, questionAuthorId, onEdit, onDelete, onReplyToReply }) => {
  const { user } = useAuthContext();
  
  // Use dynamic profile picture that updates when user changes their photo
  const { currentProfilePicture } = useCurrentProfilePicture(reply.authorId, reply.authorPhotoURL);
  
  // Editing state
  const [isEditing, setIsEditing] = useState(false);
  const [editedContent, setEditedContent] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const editorRef = useRef(null);
  const editContainerRef = useRef(null);
  const replyContentRef = useRef(null);

  // Handle link clicks with event delegation
  useEffect(() => {
    const contentElement = replyContentRef.current;
    if (!contentElement) return;

    const handleLinkClick = (e) => {
      // Check if the clicked element is a link with our data attribute
      const link = e.target.closest('a[data-external-link="true"]');
      if (link) {
        e.preventDefault();
        e.stopPropagation();
        const href = link.getAttribute('href');
        if (href) {
          window.open(href, '_blank', 'noopener,noreferrer');
        }
      }
    };

    contentElement.addEventListener('click', handleLinkClick);
    
    return () => {
      contentElement.removeEventListener('click', handleLinkClick);
    };
  }, []);

  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return formatDistanceToNow(date, { addSuffix: true });
    } catch (error) {
      console.error('Error formatting date:', error);
      return '';
    }
  };

  // Handle click outside to save edits
  useEffect(() => {
    const handleClickOutside = async (event) => {
      if (isEditing && editContainerRef.current && !editContainerRef.current.contains(event.target)) {
        await handleSaveEdits();
      }
    };

    if (isEditing) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isEditing, editedContent]);

  const handleEditContent = () => {
    setIsEditing(true);
    setEditedContent(reply.content || '');
  };

  // Edit functionality is now only available through the edit button

  const handleSaveEdits = async () => {
    if (!user?.uid || !reply?.id || isSaving) return;

    // Don't save if content hasn't changed
    if (editedContent.trim() === (reply.content || '').trim()) {
      setIsEditing(false);
      return;
    }

    setIsSaving(true);

    try {
      const result = await updateReply(reply.id, { content: editedContent.trim() }, user.uid);
      
      if (result.success) {
        // Update the local reply data if onEdit callback is provided
        if (onEdit) {
          onEdit(reply.id, { content: editedContent.trim() });
        }
        setIsEditing(false);
      } else {
        console.error('Failed to update reply:', result.error);
        // Could show error toast here
      }
    } catch (error) {
      console.error('Error updating reply:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditedContent(reply.content || '');
  };

  const handleDelete = async (replyId, userId, reason) => {
    try {
      const result = await deleteReply(replyId, userId, reason);
      if (result.success && onDelete) {
        onDelete(replyId);
      } else if (!result.success) {
        console.error('Failed to delete reply:', result.error);
        // You could show a toast notification here
      }
    } catch (error) {
      console.error('Error deleting reply:', error);
    }
  };

  const handleEdit = (replyId) => {
    handleEditContent();
  };

  return (
    <div className={`reply-item border-l-2 border-gray-100 dark:border-gray-700 pl-3 sm:pl-4 py-3 mb-1 transition-all duration-150 hover:border-gray-200 dark:hover:border-gray-600 hover:bg-gray-50/50 dark:hover:bg-gray-800/30 group ${
      reply.isOptimistic ? 'opacity-70 animate-pulse border-yellow-300 dark:border-yellow-600' : ''
    }`}>
      {/* Reply Header - Mobile Optimized */}
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center space-x-2 flex-1 min-w-0">
          <div className="relative">
            <Link
              to={`/network/${reply.authorId}`}
              target="_blank"
              rel="noopener"
              className="cursor-pointer hover:opacity-80 transition-opacity duration-200 flex-shrink-0"
            >
              {currentProfilePicture ? (
                <img
                  src={currentProfilePicture}
                  alt={reply.authorName}
                  className="h-6 w-6 sm:h-7 sm:w-7 rounded-full object-cover"
                />
              ) : (
                <div className="h-6 w-6 sm:h-7 sm:w-7 rounded-full bg-purple-100 dark:bg-purple-900 flex items-center justify-center">
                  <UserIcon className="h-3 w-3 sm:h-4 sm:w-4 text-purple-600 dark:text-purple-400" data-testid="user-icon" />
                </div>
              )}
            </Link>
            {reply.isOptimistic && (
              <div className="absolute -bottom-0.5 -right-0.5 h-2 w-2 bg-yellow-400 rounded-full border border-white dark:border-gray-800">
                <div className="h-full w-full rounded-full bg-yellow-400 animate-ping"></div>
              </div>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center space-x-1.5 mb-0.5">
              <Link
                to={`/network/${reply.authorId}`}
                target="_blank"
                rel="noopener"
                className={`text-sm font-medium hover:text-purple-600 dark:hover:text-purple-400 transition-colors duration-200 truncate ${
                  reply.authorId === questionAuthorId 
                    ? 'text-blue-600 dark:text-blue-400' 
                    : 'text-gray-900 dark:text-white'
                }`}
              >
                {reply.authorName}
              </Link>
              {reply.authorId === questionAuthorId && (
                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300 flex-shrink-0">
                  OP
                </span>
              )}
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400">
              {formatDate(reply.createdAt)}
              {reply.updatedAt && reply.updatedAt !== reply.createdAt && (
                <span className="hidden sm:inline"> • edited</span>
              )}
            </div>
          </div>
        </div>

        {/* Moderation and Deletion Controls - Always visible on mobile, hover on desktop */}
        <div className="flex items-center space-x-1 sm:space-x-2 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity duration-150 flex-shrink-0">
          <ModerationControls
            item={reply}
            type="reply"
            onModerationAction={(replyId, action) => {
              // Handle moderation action - could update reply state
              if (action === 'delete' || action === 'hide') {
                // Optionally remove from list or show hidden state
              }
            }}
          />

          <DeletionControls
            item={reply}
            type="reply"
            onDelete={handleDelete}
            onEdit={handleEdit}
          />
        </div>
      </div>

      {/* Reply Content */}
      <div className="mb-3 ml-6 sm:ml-8">
        {isEditing ? (
          <div className="relative" ref={editContainerRef}>
            <div className="mb-2 text-sm text-purple-600 dark:text-purple-300 flex items-center">
              <PencilIcon className="h-4 w-4 mr-1" /> 
              Edit your reply below (click outside to save and exit):
            </div>
            <UserMentionInput
              value={editedContent}
              onChange={setEditedContent}
              placeholder="Edit your reply. Use @ to mention other users!"
              maxLength={3000}
              error={false}
              disabled={isSaving}
              className="min-h-[120px]"
            />
            <div className="mt-2 flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
              <div className="flex items-center space-x-4">
                <button
                  onClick={handleCancelEdit}
                  disabled={isSaving}
                  className="text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveEdits}
                  disabled={isSaving}
                  className="text-purple-600 hover:text-purple-800 dark:text-purple-400 dark:hover:text-purple-200 disabled:opacity-50"
                >
                  {isSaving ? 'Saving...' : 'Save'}
                </button>
              </div>
              <span>Click outside to save automatically</span>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            {/* Reply Content */}
            {reply.content && (
              <div
                ref={replyContentRef}
                className="prose prose-sm max-w-none dark:prose-invert prose-purple plain-text-content leading-relaxed"
              >
                <ContentWithMentions 
                  content={reply.content || ''} 
                  className=""
                />
              </div>
            )}
            
            {/* Reply Attachments - TEMPORARILY HIDDEN */}
            {/* {reply.attachments && (
              <AttachmentDisplay 
                attachments={reply.attachments}
                className="ml-0"
              />
            )} */}
          </div>
        )}
      </div>

      {/* Reply Actions - Mobile Optimized */}
      <div className="ml-6 sm:ml-8">
        <MobileReplyActions
          likeCount={reply.likeCount || 0}
          targetId={reply.id}
          authorId={reply.authorId}
          onLike={onLikeUpdate}
          onReply={user ? () => onReplyToReply(reply) : undefined}
        />
      </div>
    </div>
  );
});

const ReplyList = React.forwardRef(({ questionId, questionAuthorId, onReplyCountUpdate, onEditReply, onDeleteReply, onReplySubmitted }, ref) => {
  const { user } = useAuthContext();
  const { isOpen, originalPost, parentReplyId, openModal, closeModal } = useReplyModal();
  const [replies, setReplies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [lastVisible, setLastVisible] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);

  // Performance monitoring for large threads
  usePerformanceMonitoring(replies.length);

  // Load replies
  const loadReplies = async (isLoadMore = false) => {
    if (!questionId) return;

    try {
      if (!isLoadMore) {
        setLoading(true);
        setError(null);
      } else {
        setLoadingMore(true);
      }

      const options = {
        limit: 20,
        lastVisible: isLoadMore ? lastVisible : null
      };

      const result = await getReplies(questionId, options);

      if (result.success) {
        const newReplies = result.data.replies;
        
        if (isLoadMore) {
          setReplies(prev => [...prev, ...newReplies]);
        } else {
          setReplies(newReplies);
          
          // Update parent with reply count
          if (onReplyCountUpdate) {
            onReplyCountUpdate(newReplies.length);
          }
        }

        setHasMore(result.data.hasMore);
        setLastVisible(result.data.lastVisible);
        


      } else {
        setError(result.error);
      }
    } catch (error) {
      console.error('Error loading replies:', error);
      setError('Failed to load replies');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  // Load replies when component mounts or questionId changes
  useEffect(() => {
    loadReplies();
  }, [questionId, user]);

  // Set up real-time listener for new replies
  useEffect(() => {
    if (!questionId) return;

    const repliesQuery = query(
      collection(db, 'forum_replies'),
      where('questionId', '==', questionId),
      where('status', '==', 'active'),
      orderBy('createdAt', 'asc')
    );

    const unsubscribe = onSnapshot(repliesQuery, (snapshot) => {
      if (!snapshot.metadata.hasPendingWrites) {
        // Only update if this is a server update, not a local write
        const updatedReplies = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        setReplies(updatedReplies);

        // Update parent with reply count using callback to avoid dependency
        if (onReplyCountUpdate) {
          onReplyCountUpdate(updatedReplies.length);
        }
      }
    }, (error) => {
      console.error('Error in replies listener:', error);
    });

    return () => {
      if (typeof unsubscribe === 'function') {
        try {
          unsubscribe();
        } catch (error) {
          console.warn('Error unsubscribing from replies listener:', error);
        }
      }
    };
  }, [questionId]); // Removed user and onReplyCountUpdate dependencies to prevent loop

  // Handle like updates with optimized re-rendering
  const handleLikeUpdate = React.useCallback((replyId, newLikeCount, isLiked) => {
    setReplies(prev => 
      prev.map(reply => 
        reply.id === replyId 
          ? { ...reply, likeCount: newLikeCount }
          : reply
      )
    );
  }, []);

  // Handle reply to reply
  const handleReplyToReply = React.useCallback((reply) => {
    openModal({
      id: reply.id,
      type: 'reply',
      content: reply.content,
      authorName: reply.authorName,
      authorPhotoURL: reply.authorPhotoURL,
      authorId: reply.authorId,
      createdAt: reply.createdAt
    }, reply.id);
  }, [openModal]);

  // Add new reply to the list (called from parent) with optimistic update support
  const addReply = React.useCallback((newReply, tempIdToReplace = null, error = null) => {
    setReplies(prev => {
      if (error && tempIdToReplace) {
        // Remove failed optimistic reply
        return prev.filter(reply => reply.id !== tempIdToReplace);
      }
      
      if (tempIdToReplace) {
        // Replace optimistic reply with real reply
        return prev.map(reply => 
          reply.id === tempIdToReplace ? { ...newReply, isOptimistic: false } : reply
        );
      }
      
      // Check if reply already exists to prevent duplicates
      if (prev.some(reply => reply.id === newReply.id)) {
        return prev;
      }
      
      // Add new reply (optimistic or real)
      return [...prev, newReply];
    });
    
    // Update reply count only for successful additions (not replacements or errors)
    if (!tempIdToReplace && !error && onReplyCountUpdate) {
      onReplyCountUpdate(replies.length + 1);
    }
  }, [replies.length, onReplyCountUpdate]);

  // Expose addReply method to parent component
  React.useImperativeHandle(ref, () => ({
    addReply
  }), [addReply]);

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="space-y-4">
          {[...Array(3)].map((_, index) => (
            <LoadingSkeleton key={index} className="h-32" />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <p className="text-red-600 dark:text-red-400">{error}</p>
        <button
          onClick={() => loadReplies()}
          className="mt-2 text-sm text-purple-600 hover:text-purple-700 dark:text-purple-400 dark:hover:text-purple-300"
        >
          Try again
        </button>
      </div>
    );
  }

  return (
    <div className="reply-thread">
      {replies.length === 0 ? (
        <div className="text-center py-8 px-4 bg-gradient-to-br from-purple-50/50 to-blue-50/50 dark:from-purple-900/10 dark:to-blue-900/10 rounded-lg border border-purple-100 dark:border-purple-800/30">
          <div className="mb-3">
            <ChatBubbleLeftRightIcon className="h-8 w-8 text-purple-400 dark:text-purple-500 mx-auto" />
          </div>
          <p className="text-gray-700 dark:text-gray-300 text-sm font-medium mb-1">
            💭 Be the first to share your thoughts!
          </p>
          <p className="text-gray-500 dark:text-gray-400 text-xs">
            Start the conversation by adding your reply above
          </p>
        </div>
      ) : (
        <div className="space-y-1 border-t border-gray-100 dark:border-gray-700 pt-4">
          {replies.map((reply) => (
            <ReplyCard
              key={reply.id}
              reply={reply}
              questionAuthorId={questionAuthorId}
              onLikeUpdate={handleLikeUpdate}
              onEdit={onEditReply}
              onDelete={onDeleteReply}
              onReplyToReply={handleReplyToReply}
            />
          ))}

          {hasMore && (
            <div className="text-center py-4">
              <button
                onClick={() => loadReplies(true)}
                disabled={loadingMore}
                className="px-4 py-2 text-sm font-medium text-purple-600 hover:text-purple-700 dark:text-purple-400 dark:hover:text-purple-300 disabled:opacity-50 transition-colors duration-150"
              >
                {loadingMore ? 'Loading...' : 'Load More Replies'}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Reply Modal */}
      <ReplyModal
        isOpen={isOpen}
        onClose={closeModal}
        title="Reply to Reply"
      >
        {originalPost && (
          <>
            <OriginalPostPreview post={originalPost} />
            <ReplyComposer
              questionId={questionId}
              parentReplyId={parentReplyId}
              onSubmit={(reply) => {
                closeModal();
                if (onReplySubmitted) {
                  onReplySubmitted(reply);
                }
              }}
              onCancel={closeModal}
              autoFocus={true}
            />
          </>
        )}
      </ReplyModal>
    </div>
  );
});

// Forward ref to expose addReply method
export default React.forwardRef((props, ref) => {
  const replyListRef = React.useRef();
  
  React.useImperativeHandle(ref, () => ({
    addReply: (newReply, tempIdToReplace = null, error = null) => {
      if (replyListRef.current) {
        replyListRef.current.addReply(newReply, tempIdToReplace, error);
      }
    }
  }));

  return <ReplyList ref={replyListRef} {...props} />;
});

// Performance monitoring hook for large threads
const usePerformanceMonitoring = (repliesCount) => {
  React.useEffect(() => {
    if (repliesCount > 100) {
      console.log(`Large thread detected: ${repliesCount} replies`);
      
      // Monitor render performance
      const startTime = performance.now();
      
      return () => {
        const endTime = performance.now();
        const renderTime = endTime - startTime;
        
        if (renderTime > 100) {
          console.warn(`Slow render detected: ${renderTime}ms for ${repliesCount} replies`);
        }
      };
    }
  }, [repliesCount]);
};